import { useEffect, useState } from "react";
import {
  RecaptchaVerifier,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
} from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { PageHeader, ToneBadge } from "./petaarUi";

function isVerifiedContactState(contactVerification = {}, firebaseUser = null) {
  const emailVerified = Boolean(contactVerification?.emailVerified) || Boolean(firebaseUser?.emailVerified);
  const phoneVerified = Boolean(contactVerification?.phoneVerified) || Boolean(firebaseUser?.phoneNumber);
  return {
    emailVerified,
    phoneVerified,
    anyVerified: emailVerified || phoneVerified,
  };
}

export default function Login({ onLogin, goSignup }) {
  const getAuthErrorMessage = (error) => {
    const code = String(error?.code || "");
    const fallback = String(error?.message || "Something went wrong. Please try again.");
    const map = {
      "auth/invalid-credential": "Invalid email or password.",
      "auth/user-not-found": "No account found with this email.",
      "auth/wrong-password": "Incorrect password.",
      "auth/invalid-email": "Please enter a valid email address.",
      "auth/too-many-requests": "Too many attempts. Please try again after some time.",
      "auth/operation-not-allowed": "This login method is currently disabled in Firebase Auth settings.",
      "auth/invalid-phone-number": "Invalid phone number format. Use +91XXXXXXXXXX.",
      "auth/invalid-verification-code": "Incorrect OTP. Please check and try again.",
      "auth/code-expired": "OTP expired. Please request a new OTP.",
      "auth/invalid-app-credential": "App verification failed. Use 127.0.0.1 or refresh and try again.",
      "auth/captcha-check-failed": "OTP setup failed: domain is not authorized. Add petaar.com and www.petaar.com in Firebase Auth > Settings > Authorized domains.",
      "auth/unauthorized-domain": "This domain is not authorized for OTP login. Add petaar.com and www.petaar.com in Firebase Auth > Settings.",
      "auth/quota-exceeded": "SMS quota exceeded. Please try later.",
    };
    return map[code] || fallback;
  };

  const fieldLabelStyle = {
    display: "block",
    fontWeight: 700,
    color: "var(--text)",
    marginBottom: 8,
    fontSize: 14,
    letterSpacing: 0.2,
  };
  const inputStyle = {
    width: "100%",
    borderRadius: 999,
    border: "1px solid var(--border)",
    background: "#fff",
    color: "var(--text)",
    padding: "14px 18px",
    fontSize: 16,
    outline: "none",
    boxSizing: "border-box",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.04)",
  };
  const secondaryBtn = {
    borderRadius: 999,
    border: "1px solid var(--border)",
    background: "#ffffff",
    color: "var(--text)",
    fontWeight: 700,
    padding: "12px 18px",
    minWidth: 170,
    cursor: "pointer",
    transition: "all 0.2s ease",
  };
  const roleTheme = (role) =>
    role === "provider"
      ? {
          primary: "#7f81a7",
          soft: "#e0e7ff",
          border: "#4f46e5",
          text: "#3730a3",
        }
      : {
          primary: "#ef6d34",
          soft: "#ffedd5",
          border: "#ef6d34",
          text: "#9a3412",
        };
  const [mode, setMode] = useState("email");
  const [loginRole, setLoginRole] = useState("owner");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const isLocalhost = typeof window !== "undefined" && window.location.hostname === "localhost";

  const normalizePhone = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("+")) return trimmed;
    return `+91${trimmed.replace(/\D/g, "")}`;
  };

  const setupRecaptcha = () => {
    const container = document.getElementById("login-recaptcha");
    if (!container) {
      throw new Error("reCAPTCHA container not found. Please refresh and try again.");
    }
    if (window.loginRecaptchaVerifier) {
      return window.loginRecaptchaVerifier;
    }
    window.loginRecaptchaVerifier = new RecaptchaVerifier(auth, "login-recaptcha", {
      size: "invisible",
    });
    return window.loginRecaptchaVerifier;
  };
  const clearRecaptcha = () => {
    if (window.loginRecaptchaVerifier) {
      try {
        window.loginRecaptchaVerifier.clear();
      } catch (_) {}
      window.loginRecaptchaVerifier = null;
    }
  };
  const open127Host = () => {
    if (typeof window === "undefined") return;
    const { protocol, port, pathname, search, hash } = window.location;
    window.location.assign(`${protocol}//127.0.0.1${port ? `:${port}` : ""}${pathname}${search}${hash}`);
  };

  useEffect(() => () => {
    clearRecaptcha();
  }, []);

  useEffect(() => {
    if (mode !== "phone") {
      clearRecaptcha();
      setConfirmationResult(null);
      setOtp("");
    }
  }, [mode]);

  const ensureUserDoc = async (user) => {
    const userRef = doc(db, "users", user.uid);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      await setDoc(userRef, {
        email: user.email || "",
        phone: user.phoneNumber || phone,
        role: "owner",
        providerStatus: "",
        contactVerification: {
          method: user.phoneNumber ? "phone" : "email",
          emailVerified: Boolean(user.emailVerified),
          phoneVerified: Boolean(user.phoneNumber),
        },
      });
      return { contactVerification: { emailVerified: Boolean(user.emailVerified), phoneVerified: Boolean(user.phoneNumber) } };
    }
    const existing = snap.data() || {};
    const existingContactVerification = existing.contactVerification && typeof existing.contactVerification === "object" ?
      existing.contactVerification :
      {};
    const resolved = {
      method: String(existingContactVerification.method || (user.phoneNumber ? "phone" : "email")).toLowerCase() === "phone" ? "phone" : "email",
      emailVerified: Boolean(existingContactVerification.emailVerified) || Boolean(user.emailVerified),
      phoneVerified: Boolean(existingContactVerification.phoneVerified) || Boolean(user.phoneNumber),
    };
    if (
      resolved.emailVerified !== Boolean(existingContactVerification.emailVerified) ||
      resolved.phoneVerified !== Boolean(existingContactVerification.phoneVerified) ||
      resolved.method !== String(existingContactVerification.method || "")
    ) {
      await setDoc(userRef, {
        contactVerification: resolved,
      }, { merge: true });
    }
    return { ...existing, contactVerification: resolved };
  };

  const handleLogin = async () => {
    if (mode === "email") {
      try {
        const userCred = await signInWithEmailAndPassword(auth, email, password);
        const userProfile = await ensureUserDoc(userCred.user);
        const verificationState = isVerifiedContactState(userProfile?.contactVerification, userCred.user);
        if (!verificationState.emailVerified && !verificationState.phoneVerified) {
          await sendEmailVerification(userCred.user).catch(() => {});
          await auth.signOut();
          alert("Please verify your email or phone before login. We sent a fresh verification link.");
          return;
        }
        onLogin(userCred.user);
      } catch (err) {
        alert(getAuthErrorMessage(err));
      }
      return;
    }

    if (!confirmationResult) {
      alert("Please request OTP first.");
      return;
    }
    try {
      const userCred = await confirmationResult.confirm(otp);
      await ensureUserDoc(userCred.user);
      onLogin(userCred.user);
    } catch (err) {
      alert(getAuthErrorMessage(err));
    }
  };

  const sendPhoneOtp = async () => {
    try {
      setSendingOtp(true);
      const verifier = setupRecaptcha();
      await verifier.render();
      const formattedPhone = normalizePhone(phone);
      const result = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      setConfirmationResult(result);
      setPhone(formattedPhone);
      alert("OTP sent to your mobile number.");
    } catch (err) {
      alert(getAuthErrorMessage(err));
    } finally {
      setSendingOtp(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      alert("Enter your email first.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      alert("Password reset link sent to your email.");
      setShowForgot(false);
      setResetEmail("");
    } catch (err) {
      alert(getAuthErrorMessage(err));
    }
  };

  const theme = roleTheme(loginRole);
  const primaryBtn = {
    borderRadius: 999,
    border: "none",
    background: theme.primary,
    color: "#ffffff",
    fontWeight: 800,
    fontSize: 16,
    letterSpacing: 0.2,
    padding: "14px 20px",
    width: "min(100%, 240px)",
    cursor: "pointer",
    boxShadow: loginRole === "provider" ? "0 14px 28px rgba(79, 70, 229, 0.22)" : "0 14px 28px rgba(239, 109, 52, 0.22)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  };
  const rolePillStyle = (role) => ({
    ...secondaryBtn,
    minWidth: 130,
    borderColor: loginRole === role ? theme.border : "#cbd5e1",
    background: loginRole === role ? theme.soft : "#fff",
    color: loginRole === role ? theme.text : "#475569",
  });
  const modePillStyle = (current) => ({
    ...secondaryBtn,
    minWidth: 150,
    borderColor: mode === current ? theme.border : "#cbd5e1",
    background: mode === current ? theme.soft : "#fff",
    color: mode === current ? theme.text : "#475569",
  });

  return (
    <div className="petaar-page" style={{ paddingTop: 28 }}>
      <div className="petaar-card" style={{ maxWidth: 1180, margin: "0 auto", overflow: "hidden" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 28,
            alignItems: "stretch",
          }}
        >
          <div>
            <div className="petaar-role-nav" style={{ marginBottom: 18 }}>
              <button onClick={() => setLoginRole("owner")} style={rolePillStyle("owner")}>
                Pet Owner
              </button>
              <button onClick={() => setLoginRole("provider")} style={rolePillStyle("provider")}>
                Pet Partner
              </button>
            </div>

            <PageHeader
              eyebrow={loginRole === "provider" ? "Partner Access" : "Owner Access"}
              title={loginRole === "provider" ? "Login as a Pet Partner" : "Login as a Pet Owner"}
              subtitle="Access your account using email/password or mobile OTP."
            />

            <div
              className="petaar-card"
              style={{
                marginTop: 18,
                background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(238,238,255,0.92) 100%)",
                border: `1px solid ${loginRole === "provider" ? "rgba(79, 70, 229, 0.18)" : "rgba(239, 109, 52, 0.18)"}`,
              }}
            >
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
                <button onClick={() => setMode("email")} disabled={mode === "email"} style={modePillStyle("email")}>
                  Email Login
                </button>
                <button onClick={() => setMode("phone")} disabled={mode === "phone"} style={modePillStyle("phone")}>
                  Mobile Login
                </button>
              </div>

              {mode === "email" ? (
                <div style={{ display: "grid", gap: 14, maxWidth: 560 }}>
                  <div style={{ display: "grid", gap: 6 }}>
                    <label style={fieldLabelStyle}>Email</label>
                    <input
                      placeholder="Please enter your valid email id"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    <label style={fieldLabelStyle}>Password</label>
                    <input
                      type="password"
                      placeholder="Please enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <button
                      onClick={() => setShowForgot((v) => !v)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "var(--primary)",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 700,
                        padding: 0,
                      }}
                    >
                      Forgot Password?
                    </button>
                  </div>

                  {showForgot && (
                    <div
                      style={{
                        display: "grid",
                        gap: 12,
                        padding: 18,
                        borderRadius: 18,
                        border: "1px solid var(--border)",
                        background: "rgba(255,255,255,0.92)",
                      }}
                    >
                      <div style={{ display: "grid", gap: 6 }}>
                        <label style={fieldLabelStyle}>Reset Email</label>
                        <input
                          placeholder="Enter email for reset"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          style={inputStyle}
                        />
                      </div>
                      <button onClick={handleForgotPassword} style={{ ...primaryBtn, width: "min(100%, 240px)" }}>
                        Send Reset Link
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: "grid", gap: 14, maxWidth: 560 }}>
                  {isLocalhost && (
                    <div
                      style={{
                        border: "1px solid #f5d56c",
                        background: "#fff8dc",
                        color: "#8a5b00",
                        borderRadius: 18,
                        padding: 16,
                      }}
                    >
                      Mobile OTP may fail on <strong>localhost</strong>. Use <strong>127.0.0.1</strong> for stable Firebase phone auth.
                      <div style={{ marginTop: 10 }}>
                        <button onClick={open127Host} style={{ ...secondaryBtn, width: "min(100%, 220px)" }}>Open 127.0.0.1</button>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "grid", gap: 6 }}>
                    <label style={fieldLabelStyle}>Mobile (+91...)</label>
                    <input
                      placeholder="+91 Enter mobile number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <button onClick={sendPhoneOtp} disabled={sendingOtp} style={{ ...primaryBtn, width: "min(100%, 220px)" }}>
                    {sendingOtp ? "Sending..." : "Send OTP"}
                  </button>
                  <div style={{ display: "grid", gap: 6 }}>
                    <label style={fieldLabelStyle}>Enter OTP</label>
                    <input
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 0, marginBottom: 0 }}>
                    For mobile accounts, OTP login acts as account recovery.
                  </p>
                </div>
              )}

              <div
                id="login-recaptcha"
                style={{
                  marginTop: 8,
                  visibility: mode === "phone" ? "visible" : "hidden",
                  height: mode === "phone" ? "auto" : 0,
                  overflow: "hidden",
                }}
              />

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 24 }}>
                <button onClick={handleLogin} style={{ ...primaryBtn, width: "min(100%, 240px)" }}>
                  Login
                </button>
                {goSignup && (
                  <button onClick={() => goSignup(loginRole)} style={secondaryBtn}>
                    Go to Signup
                  </button>
                )}
              </div>
            </div>
          </div>

          <aside
            className="petaar-card"
            style={{
              background: loginRole === "provider" ?
                "linear-gradient(180deg, rgba(79,70,229,0.1) 0%, rgba(255,255,255,0.95) 100%)" :
                "linear-gradient(180deg, rgba(245,166,35,0.14) 0%, rgba(255,255,255,0.95) 100%)",
              border: `1px solid ${loginRole === "provider" ? "rgba(79, 70, 229, 0.2)" : "rgba(245, 166, 35, 0.24)"}`,
              display: "grid",
              gap: 18,
              alignContent: "start",
            }}
          >
            <div>
              <ToneBadge tone={loginRole === "provider" ? "default" : "accent"}>
                {loginRole === "provider" ? "Partner Workspace" : "Pet Care Workspace"}
              </ToneBadge>
              <h2 style={{ margin: "14px 0 10px", fontSize: "2rem", lineHeight: 1.1 }}>
                {loginRole === "provider" ? "Manage services, bookings, and payouts." : "Book care, track pets, and consult vets."}
              </h2>
              <p style={{ margin: 0, color: "var(--muted)", lineHeight: 1.7 }}>
                Same login flow, cleaner entry point. Use email if you want password access, or OTP if you need a fast recovery path.
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <ToneBadge tone="success">Secure auth</ToneBadge>
              <ToneBadge tone="default">Firebase verified</ToneBadge>
              <ToneBadge tone="warning">{mode === "phone" ? "OTP mode" : "Password mode"}</ToneBadge>
            </div>

            <div
              style={{
                borderRadius: 22,
                padding: 20,
                background: "rgba(255,255,255,0.82)",
                border: "1px solid var(--border)",
                display: "grid",
                gap: 14,
              }}
            >
              <div>
                <div style={{ fontWeight: 800, fontSize: 18 }}>What you can do next</div>
                <p style={{ margin: "6px 0 0", color: "var(--muted)", lineHeight: 1.6 }}>
                  {loginRole === "provider" ?
                    "Open your provider workspace, review consultation requests, and update your availability." :
                    "Open your dashboard, manage pets, and continue active consultations without switching flows."}
                </p>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {[
                  loginRole === "provider" ? "Review live bookings" : "Track bookings and payments",
                  loginRole === "provider" ? "Respond to consultations" : "Start secure vet consults",
                  loginRole === "provider" ? "Manage service visibility" : "Save providers and services",
                ].map((item) => (
                  <div key={item} style={{ display: "flex", gap: 10, alignItems: "center", color: "var(--text)" }}>
                    <span style={{ fontSize: 18 }}>•</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
